import { CommonModule, NgForOf } from "@angular/common";
import { Component, inject, signal } from "@angular/core";
import { AuthService } from "../../../core/services/auth.service";
import printJS from 'print-js';
import {
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiButton,
  TuiDataList,
  TuiDialogService,
  TuiDropdown,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBadgedContent,
  TuiDataListWrapper,
  TuiItemsWithMore,
  TuiTabs,
} from "@taiga-ui/kit";
import {
  TuiNavigation,
  TuiSearch,
} from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import { TuiInputModule, TuiTextfieldControllerModule } from "@taiga-ui/legacy";
import { Location } from "@angular/common";
import { ForgotResetPasswordService } from "../../../core/services/forgot-reset-password.service.service";
import { ClientService } from "../../../core/services/client.service";
import { ChargesService } from "../../../core/services/charges.service";
import { ShiftService } from "../../../core/services/shift.service";
import { ProcedureRecordService } from "../../../core/services/procedure-record.service";
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";
import {
  composeMonthlyInvoiceNumber,
  formatClientIdentifier,
} from "../../../shared/utils/client-identifier";
@Component({
  selector: 'app-invoice-charges',
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
    NgForOf,
    TuiDataListWrapper,
    TuiDataList,
    TuiDropdown,
    TuiTable,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    CommonModule,
    TuiBadgedContent,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiButton,
  ],
  templateUrl: './invoice-charges.component.html',
  styleUrl: './invoice-charges.component.scss'
})
export class InvoiceChargesComponent {
  clientId: string = "";
  month: number = 0;
  year: number = 0;
  client: any | null = null;
  isLoading: boolean = false;
  ChargesRecords: any[] = [];
  ExtraChargesRecords: any[] = [];
  private readonly dialogs = inject(TuiDialogService);
  protected size: TuiSizeL | TuiSizeS = "s";
  protected open = false;
  today = new Date();
  endOfMonth: Date = new Date();
  public billingDate!: Date;

  shiftTotal : number = 0;
  extraChargeTotal : number = 0;
  loggedInUserCanEdit = false;
  allMonthDates: Date[] = [];
  private authService = inject(AuthService);

  protected readonly openDrawer = signal(false);

  constructor(
    private clientService: ClientService,
    private router: Router,
    private route: ActivatedRoute,
    private resetPasswordService: ForgotResetPasswordService,
    private chargesService: ChargesService,
    private procedureService : ProcedureRecordService,
    private location: Location,
    private shiftService: ShiftService,
  ) {
  }

  ngOnInit() {
    this.checkUserPermission();
    this.clientId = this.route.snapshot.paramMap.get("clientId")!;
    this.month = parseInt(this.route.snapshot.paramMap.get("month")!, 10);
    this.year = parseInt(this.route.snapshot.paramMap.get("year")!, 10);
    
    if (this.clientId) {
      this.loadClientData();
    }
    
    if (this.month && this.year) {
      // Load charges first, then generate month dates
      this.loadChargesRecords();
      this.loadExtraChargesRecords();
    }

    // Calculate the last day of the month
    const lastDay = new Date(this.year, this.month, 0).getDate();
    this.billingDate = new Date(this.year, this.month - 1, lastDay);
  }


  loadChargesRecords(): void {
    this.isLoading = true;
    this.shiftService.getClientShiftsBySpecificMonth(this.clientId, this.year, this.month).subscribe((res: any) => {
      if (res.success) {
        this.ChargesRecords = res.data.shifts;
        this.getNetCharge();
        // Generate month dates after shifts are loaded (only if client is already loaded)
        if (this.client) {
          this.generateAllMonthDates();
        }
      }
      this.isLoading = false;
    });
  }
  
  loadExtraChargesRecords(): void {
    this.isLoading = true;
    this.procedureService.getClientMonthlyProcedures(this.clientId, this.year, this.month).subscribe((res: any) => {
      if (true) {
        this.ExtraChargesRecords = res;
        this.getNetCharge()
      }
      this.isLoading = false;
    });
  }

  loadClientData(): void {
    this.isLoading = true;
    if (this.clientId) {
      this.clientService.getOneClientById(this.clientId).subscribe((res: any) => {
        this.client = res;
        // Generate month dates after client is loaded (if month/year are set)
        if (this.month && this.year) {
          this.generateAllMonthDates();
        }
        this.isLoading = false;
      });
    }
  }

  getNetCharge(): number {
    this.shiftTotal= this.ChargesRecords.reduce((total, shift) => total + shift.charges, 0);
    this.extraChargeTotal = this.ExtraChargesRecords.reduce((total, record) => total + (record.grandTotal || 0), 0);
    return this.shiftTotal + this.extraChargeTotal;
  }

  getTotal(): number {
    const subTotal = this.getNetCharge();
    return subTotal - this.extraChargeTotal;
  }

  getProceduresDisplay(record: any): string {
    if (record.procedures && Array.isArray(record.procedures) && record.procedures.length > 0) {
      return record.procedures.join(', ');
    }
    
    // Fallback for old data structure
    if (record.procedure) {
      return record.procedure;
    }
    
    return 'N/A';
  }

  getGrandTotal(record: any): number {
    return record.grandTotal || 0;
  }

  getPaymentBreakdown(record: any): { label: string; amount: number }[] {
    const breakdown: { label: string; amount: number }[] = [];
    const payments = record.payments || {};
    
    if (payments.doctorPayment > 0) breakdown.push({ label: 'Doctor', amount: payments.doctorPayment });
    if (payments.nursingPayment > 0) breakdown.push({ label: 'Nursing', amount: payments.nursingPayment });
    if (payments.pharmacyPayment > 0) breakdown.push({ label: 'Pharmacy', amount: payments.pharmacyPayment });
    if (payments.laboratoryPayment > 0) breakdown.push({ label: 'Laboratory', amount: payments.laboratoryPayment });
    if (payments.ambulancePayment > 0) breakdown.push({ label: 'Ambulance', amount: payments.ambulancePayment });
    if (payments.physiotherapyPayment > 0) breakdown.push({ label: 'Physiotherapy', amount: payments.physiotherapyPayment });
    if (payments.otherPayment > 0) breakdown.push({ label: 'Other', amount: payments.otherPayment });

    if (Array.isArray(record?.customPayments)) {
      for (const cp of record.customPayments) {
        if (cp?.value != null && Number(cp.value) > 0) {
          breakdown.push({ label: cp.name || 'Custom', amount: Number(cp.value) });
        }
      }
    }

    return breakdown;
  }

  getNurseName(record: any): string {
    // Check for new structure first
    if (record.nurseName) {
      return record.nurseName;
    }
    // Fallback for old structure
    return record.nurseDetails?.name || 'Unavailable';
  }

  getBillingPeriodStart(): Date {
    // Use selected month/year from route params, fallback to current month if not set
    const selectedYear = this.year || new Date().getFullYear();
    const selectedMonth = this.month ? this.month - 1 : new Date().getMonth(); // month is 1-indexed from route, convert to 0-indexed
    
    // If no client data, use 1st day of selected month
    if (!this.client) {
      return new Date(selectedYear, selectedMonth, 1);
    }

    // Check if registration date is in selected month
    const registrationDate = new Date(this.client.registrationDate);
    const isRegistrationInSelectedMonth = registrationDate.getFullYear() === selectedYear && 
                                         registrationDate.getMonth() === selectedMonth;

    // Check if renewal date is in selected month
    let isRenewalInSelectedMonth = false;
    let renewalDate = null;
    if (this.client.renewalDate) {
      renewalDate = new Date(this.client.renewalDate);
      isRenewalInSelectedMonth = renewalDate.getFullYear() === selectedYear && 
                                 renewalDate.getMonth() === selectedMonth;
    }

    // Priority: If renewal date is in selected month, use renewal date
    if (isRenewalInSelectedMonth && renewalDate) {
      return new Date(renewalDate);
    }

    // If registration date is in selected month, use registration date
    if (isRegistrationInSelectedMonth) {
      return new Date(registrationDate);
    }

    // Otherwise, use 1st day of selected month
    return new Date(selectedYear, selectedMonth, 1);
  }

  getBillingDate(): Date {
    // Use selected month/year from route params, fallback to current month if not set
    const selectedYear = this.year || new Date().getFullYear();
    const selectedMonth = this.month ? this.month - 1 : new Date().getMonth(); // month is 1-indexed from route, convert to 0-indexed
    // Return last day of selected month
    return new Date(selectedYear, selectedMonth + 1, 0);
  }

  generateAllMonthDates(): void {
    this.allMonthDates = [];
    const daysInMonth = new Date(this.year, this.month, 0).getDate();
    
    // Check if we should start from registration/renewal date
    const startDate = this.getShiftTableStartDate();
    
    for (let day = startDate; day <= daysInMonth; day++) {
      // Create UTC dates to match server's UTC date format
      this.allMonthDates.push(new Date(Date.UTC(this.year, this.month - 1, day)));
    }
  }

  getShiftTableStartDate(): number {
    // Use selected month/year from route params, fallback to current month if not set
    const selectedYear = this.year || new Date().getFullYear();
    const selectedMonth = this.month ? this.month - 1 : new Date().getMonth(); // month is 1-indexed from route, convert to 0-indexed
    
    // If no client data, start from 1st day of month
    if (!this.client) {
      return 1;
    }

    // Check if registration date is in selected month
    const registrationDate = new Date(this.client.registrationDate);
    const isRegistrationInSelectedMonth = registrationDate.getFullYear() === selectedYear && 
                                         registrationDate.getMonth() === selectedMonth;

    // Check if renewal date is in selected month
    let isRenewalInSelectedMonth = false;
    let renewalDate = null;
    if (this.client.renewalDate) {
      renewalDate = new Date(this.client.renewalDate);
      isRenewalInSelectedMonth = renewalDate.getFullYear() === selectedYear && 
                                 renewalDate.getMonth() === selectedMonth;
    }

    // Priority: If renewal date is in selected month, use renewal date
    if (isRenewalInSelectedMonth && renewalDate) {
      return renewalDate.getDate();
    }

    // If registration date is in selected month, use registration date
    if (isRegistrationInSelectedMonth) {
      return registrationDate.getDate();
    }

    // Otherwise, start from 1st day of month
    return 1;
  }

  hasShiftsForCurrentMonth(): boolean {
    return this.ChargesRecords && this.ChargesRecords.length > 0;
  }

  getShiftForDate(date: Date): any | null {
    const dateString = date.toISOString().split('T')[0];
    return this.ChargesRecords.find(shift => {
      // Extract date directly from UTC string to avoid timezone conversion
      const shiftDate = shift.date.split('T')[0];
      return shiftDate === dateString;
    }) || null;
  }

  getShiftsForDate(date: Date): any[] {
    const dateString = date.toISOString().split('T')[0];
    return this.ChargesRecords.filter(shift => {
      // Extract date directly from UTC string to avoid timezone conversion
      const shiftDate = shift.date.split('T')[0];
      return shiftDate === dateString;
    });
  }

  isOffDay(date: Date): boolean {
    return this.getShiftsForDate(date).length === 0;
  }
  

  private readonly alerts = inject(TuiAlertService);

  protected activeItemIndex = 0;

  protected onClick(item: string): void {
    this.alerts.open(item).subscribe();
  }

  goBack(): void {
    this.location.back();
  }

  printInvoice(): void {
    if (!this.client) {
      return;
    }

    const logoUrl = `${window.location.origin}/assets/logo-vertical.png`;
    const todayStr = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const admissionDateStr = this.client.admissionDate
      ? new Date(this.client.admissionDate).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

    const billingDateStr = this.getBillingDate()
      ? new Date(this.getBillingDate()).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

    const escapeHtml = (text: string): string => {
      if (!text) return "";
      return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    };

    const formatDate = (dateString: any): string => {
      if (!dateString) return "—";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    };

    // Shift Charges Table rows
    const shiftRowsHtml = this.ChargesRecords
      .map((shift) => {
        const nurseName = shift.nurseDetails?.name || "Unavailable";
        const shiftCharges = (shift.charges || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return `
        <tr>
          <td style="padding: 5px 12px; font-size: 11px; color: #1e293b;">${formatDate(shift.date)}</td>
          <td style="padding: 5px 12px; font-size: 11px; color: #1e293b;">${escapeHtml(nurseName)}</td>
          <td style="padding: 5px 12px; font-size: 11px; color: #1e293b; text-align: center;">${shift.rooster}</td>
          <td style="padding: 5px 12px; font-size: 11px; color: #1e293b;">${escapeHtml(shift.mealOption || "—")}</td>
          <td style="padding: 5px 12px; font-size: 11px; color: #1e293b;">${escapeHtml(shift.shiftType || "—")}</td>
          <td style="padding: 5px 12px; font-size: 11px; color: #1e293b; text-align: right; font-weight: 500;">LKR ${shiftCharges}</td>
        </tr>`;
      })
      .join("");

    // Additional Charges Table rows
    const additionalRowsHtml = this.ExtraChargesRecords
      .map((record) => {
        const nurseName = this.getNurseName(record);
        const description = this.getProceduresDisplay(record);
        const grandTotal = (record.grandTotal || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        
        // Breakdown html
        const breakdownItems = this.getPaymentBreakdown(record);
        let breakdownHtml = "";
        if (breakdownItems && breakdownItems.length > 0) {
          const gridItems = breakdownItems
            .map((item) => `<div style="color: #64748b; font-size: 9px; font-weight: 500;">${escapeHtml(item.label)} : ${item.amount.toFixed(2)}</div>`)
            .join("");
          breakdownHtml = `
          <tr style="background-color: #f8fafc;">
            <td colspan="4" style="padding: 4px 12px;">
              <div style="font-size: 9px; font-weight: 700; color: #64748b; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.04em;">Payment Breakdown</div>
              <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 2px 12px;">
                ${gridItems}
              </div>
            </td>
          </tr>`;
        }

        return `
        <tr>
          <td style="padding: 5px 12px; font-size: 11px; color: #1e293b;">${formatDate(record.date)}</td>
          <td style="padding: 5px 12px; font-size: 11px; color: #1e293b;">${escapeHtml(nurseName)}</td>
          <td style="padding: 5px 12px; font-size: 11px; color: #1e293b;">${escapeHtml(description)}</td>
          <td style="padding: 5px 12px; font-size: 11px; color: #2563eb; text-align: right; font-weight: 700;">LKR ${grandTotal}</td>
        </tr>
        ${breakdownHtml}`;
      })
      .join("");

    const shiftNetFormatted = Math.round(this.shiftTotal).toLocaleString("en-US");
    const extraNetFormatted = Math.round(this.extraChargeTotal).toLocaleString("en-US");
    const subTotalFormatted = Math.round(this.getNetCharge()).toLocaleString("en-US");
    const extraPaidFormatted = Math.round(this.extraChargeTotal).toLocaleString("en-US");
    const amountToBePaidFormatted = Math.round(this.getTotal()).toLocaleString("en-US");

    const html = `
      <div style="font-family:'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1e293b;padding:4px 4px 12px;line-height:1.25;display:flex;flex-direction:column;min-height:272mm;justify-content:space-between;box-sizing:border-box;">
        <div>
          <!-- Header -->
          <div style="display: flex; align-items: center; justify-content: space-between; position: relative; padding-bottom: 6px; border-bottom: 1px solid #cbd5e1;">
            <div style="width: 80px; display: flex; align-items: center; justify-content: flex-start;">
              <img src="${logoUrl}" alt="Royal Hospital Logo" style="height: 52px; width: auto; object-fit: contain;" />
            </div>
            <div style="flex: 1; text-align: center; line-height: 1.3;">
              <div style="font-size: 18px; font-weight: 800; color: #00b0ff; text-transform: uppercase; letter-spacing: 0.5px;">ROYAL HOSPITAL</div>
              <div style="font-size: 11px; font-weight: 800; color: #e91e63; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 1px;">HOME NURSING CARE</div>
              <div style="font-size: 9px; color: #475569; margin-top: 2px; font-weight: 500;">62 W A Silva Mawatha Colombo 06</div>
              <div style="font-size: 8px; color: #475569; margin-top: 1px; font-weight: 500;">Hot Line: 0713257499(WhatsApp), 0760656778, 0114000100</div>
            </div>
            <div style="width: 80px;"></div>
          </div>

          <!-- Metadata Section -->
          <div style="margin: 10px 0; font-size: 11px; display: grid; grid-template-columns: 110px 1fr auto 110px; gap: 4px 12px; line-height: 1.3;">
            <div style="color: #64748b;">Client Reg No.</div>
            <div style="font-weight: 700; color: #0f172a;">${escapeHtml(this.client.refNo || "—")}</div>
            <div style="color: #64748b; text-align: right;">Date</div>
            <div style="font-weight: 700; color: #0f172a; text-align: right;">${todayStr}</div>

            <div style="color: #64748b;">Invoice Number</div>
            <div style="font-weight: 700; color: #285ca5;">${this.generateInvoiceNumber()}</div>
            <div style="color: #64748b; text-align: right;">Admission Date</div>
            <div style="font-weight: 700; color: #0f172a; text-align: right;">${admissionDateStr}</div>

            <div style="color: #64748b;">Client Name</div>
            <div style="font-weight: 700; color: #0f172a;">${escapeHtml(this.client.name)}</div>
            <div style="color: #64748b; text-align: right;">Billing Date</div>
            <div style="font-weight: 700; color: #0f172a; text-align: right;">${billingDateStr}</div>

            <div style="color: #64748b;">Contact No</div>
            <div style="font-weight: 700; color: #0f172a; grid-column: span 3;">${escapeHtml(this.client.guardianContactNo || "—")}</div>

            <div style="color: #64748b;">Address</div>
            <div style="font-weight: 700; color: #0f172a; grid-column: span 3;">${escapeHtml(this.client.guardianAddress || "—")}</div>
          </div>

          <!-- Shift Charges Table -->
          ${this.ChargesRecords && this.ChargesRecords.length > 0 ? `
          <div style="font-size: 11px; font-weight: 700; color: #1e293b; margin: 8px 0 4px 0; text-transform: capitalize;">Shift Charges</div>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px;">
            <thead>
              <tr style="border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; background-color: #f8fafc;">
                <th style="text-align: left; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 15%;">Date</th>
                <th style="text-align: left; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 25%;">Nurse Name</th>
                <th style="text-align: center; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 12%;">Roster</th>
                <th style="text-align: left; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 18%;">Meal Option</th>
                <th style="text-align: left; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 15%;">Shift Type</th>
                <th style="text-align: right; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 15%;">Shift Charges</th>
              </tr>
            </thead>
            <tbody style="border-bottom: 1px solid #cbd5e1;">
              ${shiftRowsHtml}
            </tbody>
          </table>
          ` : ''}

          <!-- Additional Charges Table -->
          ${this.ExtraChargesRecords && this.ExtraChargesRecords.length > 0 ? `
          <div style="font-size: 11px; font-weight: 700; color: #1e293b; margin: 8px 0 4px 0; text-transform: capitalize;">Additional Charges</div>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px;">
            <thead>
              <tr style="border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; background-color: #f8fafc;">
                <th style="text-align: left; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 15%;">Date</th>
                <th style="text-align: left; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 25%;">Nurse Name</th>
                <th style="text-align: left; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 45%;">Description / Procedure</th>
                <th style="text-align: right; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 15%;">Shift Charges</th>
              </tr>
            </thead>
            <tbody style="border-bottom: 1px solid #cbd5e1;">
              ${additionalRowsHtml}
            </tbody>
          </table>
          ` : ''}

          <!-- Totals Section -->
          <div style="margin-top: 12px; display: flex; flex-direction: column; align-items: flex-end; font-size: 11px; color: #0f172a; line-height: 1.6;">
            <div style="display: grid; grid-template-columns: 180px 100px; text-align: right; gap: 2px 16px;">
              <div style="color: #475569; font-weight: 500; text-align: left;">Shift Charges (Net)</div>
              <div style="font-weight: 700; text-align: right;">${shiftNetFormatted} LKR</div>
              
              <div style="color: #475569; font-weight: 500; text-align: left;">Additional Charges (Net)</div>
              <div style="font-weight: 700; text-align: right;">${extraNetFormatted}LKR</div>
              
              <div style="font-weight: 700; text-align: left;">Sub Total</div>
              <div style="font-weight: 800; text-align: right;">${subTotalFormatted}LKR</div>
              
              <div style="color: #475569; font-weight: 500; text-align: left;">Additional Charges (Paid)</div>
              <div style="font-weight: 700; text-align: right;">${extraPaidFormatted}LKR</div>
              
              <div style="font-weight: 700; text-align: left; font-size: 12px;">Amount to be Paid</div>
              <div style="font-weight: 800; text-align: right; font-size: 12px;">${amountToBePaidFormatted}LKR</div>
            </div>
          </div>
        </div>

        <!-- Account Details Footer -->
        <div style="border-top: 1px solid #cbd5e1; padding-top: 10px; font-family: sans-serif; font-size: 11px; display: flex; gap: 40px; margin-top: auto;">
          <div style="font-weight: 700; color: #475569; width: 140px;">Account Details</div>
          <div style="color: #1e293b; line-height: 1.5;">
            Royal Hospital Home Nursing (pvt) ltd<br />
            Commercial Bank 1000650035<br />
            Wellawatta 02nd Branch<br />
            Swift Code CCEYLKLX
          </div>
        </div>
      </div>
    `;

    printJS({
      printable: html,
      type: "raw-html",
      documentTitle: `Invoice_${this.client.refNo || "Client"}_${this.month}_${this.year}`,
      scanStyles: false,
      style: `
        @page { size: A4 portrait; margin: 8mm 8mm; }
        body { margin: 0; }
      `,
    });
  }


  openDrawerWithPermission(): void {
    if (!this.loggedInUserCanEdit) {
      this.alerts.open("You do not have permission to edit.", {
        label: "Error",
        appearance: "error",
      }).subscribe();
      return;
    }
    // Use the unified Extra Charges form (procedures/add-edit).
    // Pass clientId for auto-select and a returnUrl so user can come back.
    const returnUrl = this.router.url;
    this.router.navigate(["/admin/procedures/add-edit"], {
      queryParams: { clientId: this.clientId, returnUrl },
    });
  }
 
  removeExtraCharge(id: string): void {
    this.procedureService.archiveProcedure(id).subscribe({
      next: (res) => {
        this.alerts.open("Charge Removed Successfully");
        this.loadExtraChargesRecords();
      },
      error: (err) => {
        console.error("Error removing charge:", err);
        this.alerts.open("Error removing charge");
      },
    });
  }

  restoreExtraCharge(id: string): void {
    this.procedureService.unarchiveProcedure(id).subscribe({
      next: (res) => {
        this.alerts.open("Charge Restored Successfully");
        this.loadExtraChargesRecords();
      },
      error: (err) => {
        console.error("Error restoring charge:", err);
        this.alerts.open("Error restoring charge");
      },
    });
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasPermission("EXTRA_CHARGES_CREATE");
  }

  clientIdentifierDisplay(): string {
    return formatClientIdentifier(this.client) || "Unavailable";
  }

  /**
   * Monthly invoice number for the selected billing period (reports style).
   * Example: REG0001202601 for REG-0001 in January 2026.
   */
  generateInvoiceNumber(): string {
    if (!this.client || !this.year || !this.month) {
      return "Unavailable";
    }
    return composeMonthlyInvoiceNumber(this.client, this.year, this.month);
  }
}
